<template>
  <b-container class="page">
    <b-row>
      <b-col cols="12" sm="6" offset-sm="3" lg="6" offset-lg="3">
        <div v-if="api && (api.job || api.contentShare) && channel !== 'copy'">
          <p class="text-center">{{ $t('contentShares.redirecting-to', { url: channel === 'linkedin' && (hasContentSharesV2Enabled || hasJobShareV2Enabled) ? 'Real Links Platform' : channel }).trim() }}</p>
        </div>
        <div v-else-if="api && (api.job || api.contentShare) && channel === 'copy' && url">
          <b-row>
            <b-col class="p-5">
              <b-card class="p-5 d-flex align-items-center justify-content-center">
                <div class="mx-5 d-flex flex-column">
                  <h4>Click on the button below to copy the URL</h4>
                  <b-btn class="my-2" :disabled="!url" v-clipboard:copy="url" variant="light">
                    {{ $t('share.ctas.copy-link') }} <font-awesome-icon icon="link"/>
                  </b-btn>
                  <p>Or copy the following link: {{ url }}</p>
                  <hr class="w-100"/>
                  <p><b>Unsure what to say? Here's a suggestion:</b></p>
                  <p>{{ shareText }}<br/>{{ url }}</p>
                  <p><b-btn class="p-0" variant="link" v-clipboard:copy="`${shareText}\n${url}`">☝️ Copy Suggestion</b-btn></p>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </div>
        <div v-else>
          <p class="text-center">{{ $t('general.loading') }}</p>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapRoot, mapPreferences } from '@src/mixins/root';
import gql from 'graphql-tag';
import { getShareURL } from '@/lib/share';

const ShareCodeQuery = gql`
  query ShareCodeQuery(
    $campaignWhere: CampaignWhereOneInput!
    $getJob: Boolean = false,
    $jobWhere: JobWhereOneInput!,
    $getContentShare: Boolean = false,
    $contentShareWhere: ContentShareWhereOneInput!
  ) {
    user {
      id
      emailAddress
      campaign(where: $campaignWhere) {
        id
        trackingCode
      }
      job(where: $jobWhere) @include(if:$getJob) {
        id
        title
        shareCode
        linkedInSearchURL
        customFieldsHash
      }
      contentShare(where: $contentShareWhere) @include(if:$getContentShare)  {
        id
        title
        shareCode
      }
    }
  }
`;

export default {
  props: {
    jobID: String,
    campaignID: String,
    contentShareID: String,
    channel: String
  },
  data: () => ({
    url: null,
    job: null,
    contentShare: null,
  }),
  computed: {
    ...mapRoot([ 'isMyDentist' ]),
    ...mapPreferences([ 'featuresEnabled', 'jobShareText', 'contentShareText' ]),
    shareText() {
      if (this.job || this.contentShare) {
        const { job, contentShare } = this;
        const { customFieldsHash: jobCustomFieldsHash } = job || {};
        const { customFieldsHash: contentShareCustomFieldsHash } = contentShare || {};
        const { jobShareText } = jobCustomFieldsHash || {};
        const { contentShareText } = contentShareCustomFieldsHash || {};
        const shareText = jobShareText || contentShareText || (job ? this.jobShareText : this.contentShareText) || null;
        return shareText ? shareText
          .replace('{jobTitle}', (job || {}).title)
          .replace('{contentShareTitle}', (contentShare || {}).title)
          .replace('{contentTitle}', (contentShare || {}).title)
          : null;
      }
      return null;
    },
    hasContentSharesV2Enabled() {
      return (this.featuresEnabled || []).includes('content-share-v2');
    },
    hasJobShareV2Enabled() {
      return (this.featuresEnabled || []).includes('job-share-v2');
    },
  },
  apollo: {
    api: {
      query: ShareCodeQuery,
      variables() {
        const { jobID, contentShareID, campaignID } = this;
        return {
          getJob: !!jobID,
          jobWhere: jobID ? { id: jobID } : {},
          getContentShare: !!contentShareID,
          contentShareWhere: contentShareID ? { id: contentShareID } : {},
          campaignWhere: { id: campaignID }
        };
      },
      update(data) {
        const { job, contentShare } = data ? data.user : {};
        return { job, contentShare };
      },
      async result({ loading, data }) {
        if (!loading && data && data.user) {
          try {
            const { jobID, campaignID, contentShareID, channel } = this;
            const { job: j, contentShare: c, campaign } = data.user;
            this.job = j;
            this.contentShare = c;
            console.log({ user: data.user });
            this.$assert((j && j.shareCode) || (c && c.shareCode), new Error('Share code cannot be retrieved.'));

            const { compID } = this.$route.query || {};

            const defaultEventName = jobID ? 'JOB_SHARED' : 'CONTENT_SHARED';
            const eventName = channel === 'linkedinsearch' ? 'JOB_BROWSE_LINKEDIN' : defaultEventName;

            await this.$root.captureLog({
              eventName,
              job: jobID ? { id: jobID } : undefined,
              contentShare: contentShareID ? { id: contentShareID } : undefined,
              campaign: campaignID ? { id: campaignID } : undefined,
              campaignComponent: compID ? { id: compID } : undefined,
              user: { self: true },
              dataHash: { channel, shareSource: 'offline' },
            });

            const baseURL = origin.includes('localhost') ? 'https://referrals.reallinks.dev' : origin;
            const contentShareText = 'We are hiring. Apply to one of our openings!';
            const jobShareText = 'Do you want to work with me? Checkout the following job opening!';
            const trackingExtension = campaign && campaign.trackingCode ? `?${campaign.trackingCode}` : null;
            if (channel !== 'copy') {
              let redirectURL = null;
              if (channel === 'see-content' && c && c.shareCode) {
                redirectURL = `${baseURL}/c/${c.shareCode}`;
              } else if (channel === 'linkedin' && (jobID || contentShareID) && (this.hasContentSharesV2Enabled || this.hasJobShareV2Enabled)) {
                if (jobID) {
                  redirectURL = `${baseURL}/campaign-jobs?j=${jobID}&comms=true`;
                } else {
                  redirectURL = `${baseURL}/campaign-content-share?c=${contentShareID}&comms=true`;
                }
              } else if (j && j.shareCode && channel !== 'linkedinsearch') {
                const shareUrl = `${baseURL}/r/${j.shareCode}${trackingExtension ? `${trackingExtension}` : ''}`;
                console.log({ shareUrl });
                redirectURL = getShareURL(this.$i18n, { channel, shareText: jobShareText, shareUrl }).replace('www.', '');
              } else if (c && c.shareCode && channel !== 'linkedinsearch') {
                const shareUrl = `${baseURL}/c/${c.shareCode}${trackingExtension ? `${trackingExtension}` : ''}`;
                redirectURL = getShareURL(this.$i18n, { channel, shareText: contentShareText, shareUrl }).replace('www.', '');
                console.log({ shareUrl });
              } else if (channel === 'linkedinsearch') {
                const connectionsURL = 'https://www.linkedin.com/mynetwork/invite-connect/connections/';
                redirectURL = j.customFieldsHash && j.customFieldsHash.linkedInSearchURL ? j.customFieldsHash.linkedInSearchURL : (j.linkedInSearchURL || connectionsURL);
              }
              console.log({ redirectURL });
              window.location = redirectURL;
            } else {
              if (j && j.shareCode) {
                this.url = `${baseURL}/r/${j.shareCode}`;
              } else if (c && c.shareCode) {
                this.url = `${baseURL}/c/${c.shareCode}`;
                if (this.isMyDentist) {
                  window.location = this.url;
                }
              }
            }
          } catch (error) {
            this.$notifyErr(error);
          }
        }
      },
      error(err) {
        this.err = err;
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/User/ShareRedirect.vue